import {
  EmissionsReductions as EmissionsReductionsT,
  EmissionsReductionIntervention,
  InterventionCategory,
} from '@/types';
import { useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { cloneDeep, sortBy } from 'lodash/fp';
import { useTheme } from 'styled-components';
import { Content, Stack, Inline, InlineItem } from '@kinesis/bungle';
//import { TextBlock } from '@/components/text-block';
import { Title } from '@/components/title';
import { renderValue, renderUnit } from '@/data/units';
import { renderVisualisationTooltip } from '@/components/visualisation-tooltip';
import { EmissionsReductionsLegend } from './emissions-reductions-legend';
import {
  renderInterventionCategory,
  interventionCategories,
} from '@/data/labels';
import { categoryColors } from '@/data/colors';

const EmissionsReductions = (props: EmissionsReductionsProps) => {
  const theme = useTheme() as any;
  const { reductions: reductionsProp } = props;
  const [selectedPolicy, setSelectedPolicy] = useState<
    'current' | 'intermediate' | 'aggressive'
  >('current');

  const options = useMemo(() => {
    const reductions = cloneDeep(reductionsProp);

    const focus = sortBy(
      (i) => interventionCategories.indexOf(i.area),
      reductions[selectedPolicy],
    ) as EmissionsReductionIntervention[];
    const categoryLabels = focus.map(
      (v: EmissionsReductionIntervention) => v.name,
    );
    return {
      plotOptions: {
        series: {
          animation: false,
          states: {
            inactive: { opacity: 1 },
          },
        },
        bar: {
          centerInCategory: true,
          pointWidth: 32,
          borderWidth: 1,
          states: {
            hover: { enabled: false },
          },
          // TODO REVIEW?
          pointPadding: 0.1,
          groupPadding: 0.1,
        },
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        crosshair: {
          color: theme.color.gray3,
          zIndex: -1,
        },
        categories: categoryLabels,
        gridLineWidth: 1,
        gridLineColor: theme.colors.gray4.hex,
        lineColor: theme.colors.gray5.hex,
        title: { enabled: false },
        tickWidth: 1,
        tickLength: 9,
        tickColor: theme.colors.gray5.hex,
        labels: {
          x: -8,
          useHTML: true,
          style: {
            fontSize: '12px',
            lineHeight: '20px',
            textAlign: 'right',
            color: 'rgba(0,0,0,0.65)',
          },
        },
      },
      yAxis: {
        plotLines: [
          // to stop borders of bars near 0 from obscuring the gridline
          { color: theme.colors.gray4.hex, width: 1, value: 0, zIndex: 20 },
        ],
        gridLineWidth: 1,
        gridLineColor: theme.colors.gray4.hex,
        lineColor: theme.colors.gray5.hex,
        lineWidth: 1,
        labels: {
          useHTML: true,
          rotation: 0,
          y: 17,
          style: {
            color: 'rgba(0,0,0,0.65)',
            fontWeight: 400,
            lineHeight: '20px',
            fontSize: '12px',
          },
          formatter(o: any) {
            return renderValue(o.value, reductions.unit);
          },
        },
        title: {
          useHTML: true,
          text: `Emissions reduction (${renderUnit(reductions.unit)})`,
          style: {
            color: 'rgba(0,0,0,0.85)',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '20px',
          },
          y: 8, // move title to edge
        },
      },
      credits: { enabled: false },
      title: { text: undefined },
      lang: {
        noData: 'There is no data.',
      },
      noData: {
        style: {
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          color: 'rgba(0,0,0,0.25)',
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(255,255,255,0)',
        borderWidth: 0,
        shadow: false,
        animation: false,
        hideDelay: 0,
        outside: true,
        padding: 0,
        formatter(): any {
          const o = this as any;
          return renderVisualisationTooltip({
            title: `${o.x}`,
            entries: o.points.map((point: any) => ({
              color: point.series.color,
              label: point.series.name,
              value: point.y,
              unit: reductions.unit,
            })),
          });
        },
      },
      chart: {
        height: 1100,
        marginLeft: 200,
        type: 'bar',
        animation: false,
        spacing: [1, 1, 10, 1],
        plotBorderWidth: 0,
        plotBorderColor: theme.colors.gray4.hex,
        plotBackgroundColor: `rgba(0,0,0,0)`,
        style: {
          // eslint-disable-next-line max-len
          fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
          fontSize: '12px',
        },
      },
      series: interventionCategories.map((c: InterventionCategory) => ({
        color: categoryColors[c],
        name: renderInterventionCategory(c),
        data: focus
          .map((v: EmissionsReductionIntervention, index: number) => ({
            x: index,
            y: v.value,
            name: v.name,
            area: v.area,
          }))
          .filter((v: any) => v.area == c),
      })),
    };
  }, [theme, reductionsProp, selectedPolicy]);
  return (
    <Stack space='medium'>
      <Title>Cumulative emissions reductions by 2050</Title>
      {/* <TextBlock>Chart description TBC..</TextBlock>*/}
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <Stack space='medium'>
          <Inline alignY='top'>
            <InlineItem sizing='fill-container'>
              <Content>
                <HighchartsReact highcharts={Highcharts} options={options} />
              </Content>
            </InlineItem>
            <InlineItem sizing='fit-content'>
              <EmissionsReductionsLegend
                value={selectedPolicy}
                onChange={setSelectedPolicy}
              />
            </InlineItem>
          </Inline>
        </Stack>
      </Content>
    </Stack>
  );
};

export type EmissionsReductionsProps = {
  reductions: EmissionsReductionsT;
};

export { EmissionsReductions };
